import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Ruler, ArrowRight } from "lucide-react";
import SEO from "@/components/storefront/SEO";

const QUILT_PRESETS = [
  ["Baby", 36, 48],
  ["Throw", 54, 66],
  ["Twin", 70, 90],
  ["Queen", 90, 100],
  ["King", 108, 100],
];

function roundQuarterYard(y) {
  return Math.ceil(y * 4) / 4;
}

function BackingBindingCalc() {
  const [w, setW] = useState(54);
  const [h, setH] = useState(66);

  const res = useMemo(() => {
    const W = Number(w) || 0;
    const H = Number(h) || 0;
    if (W < 10 || H < 10 || W > 200 || H > 200) return null;
    const usable = 40; // usable width of fabric after selvages, conservative
    const needW = W + 8; // 4" overhang each side
    const needH = H + 8;
    // Backing: vertical panels of length needH
    const panels = Math.max(1, Math.ceil(needW / usable));
    const backingYards = roundQuarterYard((panels * needH) / 36);
    // Binding: 2.5" strips cut WOF
    const perimeter = 2 * (W + H) + 10; // +10 for corners & joins
    const strips = Math.ceil(perimeter / usable);
    const bindingYards = roundQuarterYard((strips * 2.5) / 36);
    return { panels, backingYards, strips, bindingYards, perimeter };
  }, [w, h]);

  return (
    <div className="rounded-md border border-[#E6D4CC] bg-white p-6 md:p-8" data-testid="backing-calc">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1E3DC] text-[#6D2E4B]"><Ruler size={20} /></div>
        <div>
          <h2 className="font-display text-2xl text-[#2B2127]">Backing & binding calculator</h2>
          <p className="text-[13px] text-[#6B5158]">Assumes 40" usable width of fabric, 4" backing overhang per side, 2.5" binding strips.</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {QUILT_PRESETS.map(([label, pw, ph]) => (
          <button
            key={label}
            onClick={() => { setW(pw); setH(ph); }}
            className={`rounded-full border px-4 py-1.5 text-[13px] transition-colors ${Number(w) === pw && Number(h) === ph ? "border-[#6D2E4B] bg-[#6D2E4B] text-white" : "border-[#E6D4CC] bg-[#FAF6F1] text-[#3A2E33] hover:border-[#9D5C6C]"}`}
            data-testid={`preset-${label.toLowerCase()}`}
          >
            {label} {pw}×{ph}
          </button>
        ))}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Quilt width (inches)</span>
          <input type="number" min="10" max="200" value={w} onChange={(e) => setW(e.target.value)}
            className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="calc-width" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Quilt height (inches)</span>
          <input type="number" min="10" max="200" value={h} onChange={(e) => setH(e.target.value)}
            className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="calc-height" />
        </label>
      </div>

      {res ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="calc-results">
          <div className="rounded-md bg-[#F9F1EB] p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B5158]">Backing</div>
            <div className="mt-1 font-display text-3xl text-[#6D2E4B]" data-testid="backing-yards">{res.backingYards} yd</div>
            <div className="mt-1 text-[13px] leading-relaxed text-[#3A2E33]">
              {res.panels} vertical panel{res.panels > 1 ? "s" : ""} of {Number(h) + 8}" each{res.panels > 1 ? ", seamed side by side" : ""}.
            </div>
          </div>
          <div className="rounded-md bg-[#F9F1EB] p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B5158]">Binding</div>
            <div className="mt-1 font-display text-3xl text-[#6D2E4B]" data-testid="binding-yards">{res.bindingYards} yd</div>
            <div className="mt-1 text-[13px] leading-relaxed text-[#3A2E33]">
              {res.strips} strips × 2.5" WOF (perimeter ≈ {res.perimeter}" incl. corners and joins).
            </div>
          </div>
        </div>
      ) : (
        <p className="mt-6 text-sm text-[#9D5C6C]" data-testid="calc-invalid">Enter a quilt size between 10" and 200" per side.</p>
      )}
    </div>
  );
}

function FatQuarterCalc() {
  const [w, setW] = useState(54);
  const [h, setH] = useState(66);
  const [block, setBlock] = useState(6);

  const res = useMemo(() => {
    const W = Number(w) || 0;
    const H = Number(h) || 0;
    const B = Number(block) || 0;
    if (W < 10 || H < 10 || B < 2 || B > 20) return null;
    const cut = B + 0.5; // seam allowance 1/4" per side
    const blocksAcross = Math.ceil(W / B);
    const blocksDown = Math.ceil(H / B);
    const totalBlocks = blocksAcross * blocksDown;
    const perFq = Math.floor(18 / cut) * Math.floor(22 / cut);
    if (perFq < 1) return { invalidBlock: true };
    const fqNeeded = Math.ceil(totalBlocks / perFq);
    return { totalBlocks, blocksAcross, blocksDown, perFq, fqNeeded, cut };
  }, [w, h, block]);

  return (
    <div className="rounded-md border border-[#E6D4CC] bg-white p-6 md:p-8" data-testid="fq-calc">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1E3DC] text-[#6D2E4B]"><Calculator size={20} /></div>
        <div>
          <h2 className="font-display text-2xl text-[#2B2127]">Fat quarter math</h2>
          <p className="text-[13px] text-[#6B5158]">How many 18" × 22" fat quarters for a simple square-block top. Cut size = finished block + ½" seam allowance.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Top width (in)</span>
          <input type="number" min="10" max="200" value={w} onChange={(e) => setW(e.target.value)}
            className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="fq-width" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Top height (in)</span>
          <input type="number" min="10" max="200" value={h} onChange={(e) => setH(e.target.value)}
            className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="fq-height" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Finished block (in)</span>
          <input type="number" min="2" max="20" step="0.5" value={block} onChange={(e) => setBlock(e.target.value)}
            className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" data-testid="fq-block" />
        </label>
      </div>

      {res && !res.invalidBlock ? (
        <div className="mt-6 rounded-md bg-[#F9F1EB] p-5" data-testid="fq-results">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#6B5158]">Fat quarters needed</div>
              <div className="mt-1 font-display text-4xl text-[#6D2E4B]" data-testid="fq-needed">{res.fqNeeded}</div>
            </div>
            <div className="text-[13px] leading-relaxed text-[#3A2E33]">
              {res.totalBlocks} blocks total ({res.blocksAcross} × {res.blocksDown}) cut at {res.cut}" ·
              each fat quarter yields {res.perFq} block{res.perFq > 1 ? "s" : ""}.
            </div>
          </div>
          <p className="mt-3 text-[12px] text-[#6B5158]">Tip: add 1–2 extra FQs for fussy cutting, mistakes, and the scrap bin — future you says thanks.</p>
        </div>
      ) : (
        <p className="mt-6 text-sm text-[#9D5C6C]">{res?.invalidBlock ? "That block is too large to cut from a fat quarter — try 8\" or smaller." : "Enter a top size (10–200\") and a block size (2–20\")."}</p>
      )}
    </div>
  );
}

export default function QuiltCalculators() {
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-14 md:py-20" data-testid="calculators-page">
      <SEO
        title="Quilt Calculators — Backing, Binding & Fat Quarters"
        description="Free quilting calculators: how many yards of backing and binding your quilt needs, and how many fat quarters for a square-block top. Baby to king presets."
      />
      <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Maker resources</div>
      <h1 className="font-display text-4xl leading-tight md:text-[64px]">
        Quilt <span className="font-display-soft italic">calculators</span>
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#3A2E33]">
        The two questions every quilter asks at the cutting table: "how much backing?" and "how many fat quarters?"
        Answered in seconds, with honest math and conservative 40" usable width. Estimates — always buy a touch extra
        for directional prints.
      </p>

      <div className="mt-10 space-y-8">
        <BackingBindingCalc />
        <FatQuarterCalc />
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Link to="/guides/precut-sizes" className="inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-6 py-3 text-sm text-white hover:bg-[#59253d]">
          Precut size guide <ArrowRight size={14} />
        </Link>
        <Link to="/c/fabric" className="text-sm text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">
          Shop fabric
        </Link>
      </div>
    </div>
  );
}
